// TabsObjetivos.jsx
import React, { useState, useEffect } from "react";
import { Box, Tabs, Tab, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GetObjetivosAprendizaje from "../ObjetivosAprendizaje/GetObjetivosAprendizaje";
import { useProyecto } from "../../data/ProyectoContext";

/**
 * Componente auxiliar para renderizar el contenido de cada pestaña.
 */
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tab-panel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

/**
 * Componente que administra las pestañas de asignaturas.
 *
 * Props:
 * - nombreNivel: Nombre del nivel (para pasar a GetObjetivosAprendizaje).
 * - asignaturaSeleccionada: Objeto que representa la asignatura seleccionada.
 * - setPromptData, objetivosSeleccionados, setObjetivosSeleccionados, setIsOAAdded: Props para manejar
 *   los datos y acciones dentro del componente GetObjetivosAprendizaje.
 */
export default function TabsObjetivos({
  nombreNivel,
  asignaturaSeleccionada,
  setPromptData,
  objetivosSeleccionados,
  setObjetivosSeleccionados,
  setIsOAAdded,
}) {
  // Arreglo para almacenar las asignaturas que se muestran como pestañas.
  const [tabs, setTabs] = useState([]);
  // Estado para controlar el índice de la pestaña activa.
  const [value, setValue] = useState(0);

  // Obtener el estado del proyecto y su función de actualización desde el contexto.
  const { setProyecto } = useProyecto();

  // useEffect para detectar cuando se selecciona una nueva asignatura.
  // Se dispara únicamente cuando "asignaturaSeleccionada" cambia.
  useEffect(() => {
    if (asignaturaSeleccionada && asignaturaSeleccionada.nombre) {
      setTabs((prevTabs) => {
        const tabIndex = prevTabs.findIndex(
          (tab) => tab.nombreAsignatura === asignaturaSeleccionada.nombre
        );
        if (tabIndex === -1) {
          const newTabs = [
            ...prevTabs,
            { nombreAsignatura: asignaturaSeleccionada.nombre },
          ];
          setValue(newTabs.length - 1);
          return newTabs;
        } else {
          setValue(tabIndex);
          return prevTabs;
        }
      });
    }
  }, [asignaturaSeleccionada]);

  // Actualizar el campo "materias" del proyecto cada vez que cambian las pestañas.
  useEffect(() => {
    setProyecto((prevProyecto) => ({
      ...prevProyecto,
      materias: tabs.map((tab) => tab.nombreAsignatura),
    }));
  }, [tabs, setProyecto]);

  // Función para manejar el cambio de pestaña.
  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };

  // Función para eliminar una pestaña y limpiar los objetivos asociados.
  const handleTabDelete = (deleteIndex) => {
    // Se obtiene la pestaña a eliminar.
    const tabToDelete = tabs[deleteIndex];

    // Eliminar los objetivos locales asociados a la materia eliminada.
    if (objetivosSeleccionados && Array.isArray(objetivosSeleccionados)) {
      const updatedObjetivos = objetivosSeleccionados.filter(
        (item) => item.nombreAsignatura !== tabToDelete.nombreAsignatura
      );
      setObjetivosSeleccionados(updatedObjetivos);
    }

    // Eliminar la pestaña.
    const newTabs = tabs.filter((_, index) => index !== deleteIndex);
    setTabs(newTabs);

    // Ajustar el índice de la pestaña activa.
    if (value === deleteIndex) {
      setValue(
        newTabs.length > 0 ? (deleteIndex === 0 ? 0 : deleteIndex - 1) : 0
      );
    } else if (value > deleteIndex) {
      setValue(value - 1);
    }

    // Actualizar el contexto del proyecto, eliminando del array "objetivos_aprendizaje"
    // los objetivos que tengan la propiedad "materia" igual al nombre de la asignatura eliminada.
    setProyecto((prevProyecto) => ({
      ...prevProyecto,
      objetivos_aprendizaje: prevProyecto.objetivos_aprendizaje.filter(
        (item) => item.materia !== tabToDelete.nombreAsignatura
      ),
    }));
  };

  return (
    <Box>
      {/* Barra de pestañas */}
      <Tabs
        value={value}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Objetivos por asignatura"
      >
        {tabs.map((tab, index) => (
          <Tab
            key={index}
            // Se utiliza un componente personalizado en el label para incluir el botón de eliminar.
            label={
              <Box display="flex" alignItems="center">
                {tab.nombreAsignatura}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Evitar que al presionar el botón se active la pestaña.
                    handleTabDelete(index);
                  }}
                  sx={{ ml: 1 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            }
            id={`tab-${index}`}
            aria-controls={`tab-panel-${index}`}
          />
        ))}
      </Tabs>
      {/* Renderizado del contenido de cada pestaña */}
      {tabs.map((tab, index) => (
        <TabPanel key={index} value={value} index={index}>
          <GetObjetivosAprendizaje
            nombreAsignatura={tab.nombreAsignatura}
            nombreNivel={nombreNivel}
            setPromptData={setPromptData}
            objetivosSeleccionados={objetivosSeleccionados}
            setObjetivosSeleccionados={setObjetivosSeleccionados}
            setIsOAAdded={setIsOAAdded}
          />
        </TabPanel>
      ))}
    </Box>
  );
}
